/**
 * Donation Thank-You page (/donate/thank-you).
 *
 * Stripe Checkout's success_url redirects here with ?session_id=cs_xxx.
 * We poll /api/donations/checkout/status until the session flips to
 * `paid`, then render an emotional confirmation page tailored to the
 * dedication type the donor chose.
 *
 * The PDF dedication certificate (planned for Task 3) and the receipt
 * email (Task 4) are NOT implemented yet — we deliberately do not show
 * a "coming soon" stub for the PDF button because that turns a sacred
 * moment into a tease. The button will appear when the feature is real.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Heart, Share2, BookOpen, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import BrandMark from "../components/BrandMark";
import CertificateViewer from "../components/CertificateViewer";

const CURRENCY_SYMBOL = { ILS: "₪", USD: "$" };

// Map each dedication type to a verse the donor would recognize as
// fitting the spiritual intent of their gift. The verse is rendered in
// the verse-font with nikud so it reads as authentic Tanakh, not as
// generic body copy.
const DEDICATION_VERSES = {
  memory: {
    he: "וְעַמֵּךְ כֻּלָּם צַדִּיקִים לְעוֹלָם יִירְשׁוּ אָרֶץ",
    ref: "ישעיהו ס׳:כ״א",
  },
  honor: {
    he: "וְהִצְלַחְתָּ אֶת דְּרָכֶיךָ וְאָז תַּשְׂכִּיל",
    ref: "יהושע א׳:ח׳",
  },
  healing: {
    he: "רְפָאֵנִי יְהוָה וְאֵרָפֵא",
    ref: "ירמיהו י״ז:י״ד",
  },
  none: {
    he: "וְכָל מַעֲשֶׂיךָ יִהְיוּ לְשֵׁם שָׁמַיִם",
    ref: "פרקי אבות ב׳:י״ב",
  },
};

// Hebrew labels for each dedication type — used both in the headline
// and the donation-details summary block below.
const DEDICATION_LABEL = {
  memory: "לעילוי נשמת",
  honor: "להצלחת",
  healing: "לרפואת",
};

export default function DonateThankYou() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  // pollState mirrors the lifecycle of the Stripe session lookup:
  //   "polling" — still asking the backend
  //   "paid"    — donation confirmed → show full thank-you
  //   "failed"  — gave up after maxAttempts
  //   "expired" — Stripe says the checkout session expired
  //   "missing" — landed here without a session_id at all
  const [pollState, setPollState] = useState(sessionId ? "polling" : "missing");
  const [donation, setDonation] = useState(null);
  const [certOpen, setCertOpen] = useState(false);
  // Polling lifecycle. StrictMode in dev re-runs effects (mount→unmount
  // →mount), and each cleanup flips cancelledRef → true. We re-arm
  // cancelledRef on every mount so that a stale "true" from the first
  // pass doesn't poison the second pass's in-flight request.
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    cancelledRef.current = false;

    let attempts = 0;
    // Live-mode Stripe payments can take 15-45 seconds to register as
    // "paid" depending on payment method (3DS auth, bank rails, etc.).
    // The previous 8×2s=16s window was too short and timed donors out
    // mid-charge → user reported being charged $5 with a "failed" UI.
    // We now poll up to ~90 seconds with linear-then-quadratic backoff.
    const maxAttempts = 25;
    const tick = async () => {
      if (cancelledRef.current) return;
      attempts += 1;
      try {
        const res = await api.checkoutStatus(sessionId);
        if (cancelledRef.current) return;
        if (res.payment_status === "paid") {
          setDonation(res.donation);
          setPollState("paid");
          setSearchParams({}, { replace: true });
          return;
        }
        if (res.session_status === "expired") {
          setPollState("expired");
          setSearchParams({}, { replace: true });
          return;
        }
        if (attempts >= maxAttempts) {
          setPollState("failed");
          return;
        }
        // 2s for first 8 attempts, then 4s — keeps total wait <= ~90s
        setTimeout(tick, attempts < 8 ? 2000 : 4000);
      } catch {
        if (cancelledRef.current) return;
        if (attempts >= maxAttempts) {
          setPollState("failed");
          return;
        }
        setTimeout(tick, attempts < 8 ? 2000 : 4000);
      }
    };
    tick();
    return () => { cancelledRef.current = true; };
  }, [sessionId, setSearchParams]);

  if (pollState === "polling") return <PollingView />;
  if (pollState === "failed") return <FailedView />;
  if (pollState === "expired") return <ExpiredView />;
  if (pollState === "missing") return <MissingView />;
  if (donation && pollState === "paid") {
    return <PaidView donation={donation} />;
  }
  return null;
}

// ---------- States ----------

function PollingView() {
  return (
    <div
      className="mx-auto max-w-md px-4 py-24 text-center"
      data-testid="thank-you-polling"
    >
      <Loader2 className="h-8 w-8 mx-auto animate-spin text-gold mb-4" strokeWidth={1.5} />
      <h2 className="font-heading text-2xl mb-2">מאמת תשלום...</h2>
      <p className="text-sm text-muted-foreground">רגע אחד, מתחבר ל-Stripe</p>
    </div>
  );
}

function FailedView() {
  return (
    <div
      className="mx-auto max-w-md px-4 py-24 text-center"
      data-testid="thank-you-failed"
    >
      <XCircle className="h-10 w-10 mx-auto text-amber-500/80 mb-4" strokeWidth={1.5} />
      <h2 className="font-heading text-2xl mb-3">האישור עדיין בעיבוד</h2>
      <p className="text-sm text-muted-foreground mb-7 leading-relaxed">
        ייתכן שהבנק עדיין מאמת את התשלום (זה עלול לקחת מספר דקות).
        <br />
        <strong>אם חויבת בכרטיס</strong> — התרומה תופיע בהיסטוריית התרומות שלך
        תוך כמה דקות, וקבלה תישלח לאימייל שלך.
        <br /><br />
        לא קיבלת קבלה תוך 10 דקות? צור איתנו קשר:{" "}
        <a href="mailto:7torahya@gmail.com" className="text-gold underline">
          7torahya@gmail.com
        </a>
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button asChild data-testid="thank-you-failed-retry">
          <Link to="/donate">נסה שוב</Link>
        </Button>
        <Button
          asChild
          variant="outline"
          className="border-gold/40 hover:bg-gold/10 hover:border-gold"
          data-testid="thank-you-failed-back"
        >
          <Link to="/beit-midrash">
            <BookOpen className="h-4 w-4 ms-2" strokeWidth={1.5} />
            חזרה לבית המדרש
          </Link>
        </Button>
      </div>
    </div>
  );
}

function ExpiredView() {
  return (
    <div
      className="mx-auto max-w-md px-4 py-24 text-center"
      data-testid="thank-you-expired"
    >
      <XCircle className="h-10 w-10 mx-auto text-amber-500/80 mb-4" strokeWidth={1.5} />
      <h2 className="font-heading text-2xl mb-2">פג תוקפו של תהליך התשלום</h2>
      <p className="text-sm text-muted-foreground mb-6">
        ניתן לפתוח תרומה חדשה בכל עת.
      </p>
      <Button asChild>
        <Link to="/donate">חזרה לדף תרומות</Link>
      </Button>
    </div>
  );
}

function MissingView() {
  return (
    <div
      className="mx-auto max-w-md px-4 py-24 text-center"
      data-testid="thank-you-missing"
    >
      <h2 className="font-heading text-2xl mb-2">דף תודה</h2>
      <p className="text-sm text-muted-foreground mb-6">
        דף זה נטען רק אחרי תרומה. אנו מזמינים אותך לתרום.
      </p>
      <Button asChild>
        <Link to="/donate">לדף תרומות</Link>
      </Button>
    </div>
  );
}

// ---------- Paid (the real thank-you experience) ----------

function PaidView({ donation }) {
  const dedicationType = donation.dedication_type || "none";
  const dedicationText = (donation.dedication_text || "").trim();
  const verse = DEDICATION_VERSES[dedicationType] || DEDICATION_VERSES.none;

  // Headline composition. We compose by piece (not a single template
  // string) so the dedication name reads with a soft visual emphasis,
  // matching how Hebrew honorifics are typically printed.
  const headline = buildHeadline(dedicationType, dedicationText);

  // Both dates rendered together — Hebrew calendar for the religious
  // context, Gregorian as the receipt-friendly anchor donors recognise
  // for their records.
  const created = new Date(donation.created_at);
  const hebrewDate = formatHebrewDate(created);
  const gregorianDate = created.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // Backend now renders a general "thank you" PDF for every completed
  // donation, not just dedications, so we surface the certificate
  // button to all donors — admins do the same. The PDF opens inside
  // the in-app `<CertificateViewer>` (Dialog + X) so the donor isn't
  // stranded in the browser's native PDF viewer with no way back.
  const certificateUrl = donation.stripe_session_id
    ? `${BACKEND_URL}/api/donations/${donation.stripe_session_id}/certificate.pdf`
    : null;

  const onShare = async () => {
    const shareData = {
      title: "תוריא — תנ\"ך אינטראקטיבי",
      text: "גלו את תוריא — לימוד תנ\"ך עם פרשנויות מסורתיות, פירוש חכם, ומחברת אישית.",
      url: window.location.origin,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch {
      // user cancelled / share failed — fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(shareData.url);
      toast.success("הקישור הועתק. שתפו עם חברים");
    } catch {
      toast.info(shareData.url);
    }
  };

  return (
    <div
      className="mx-auto max-w-2xl px-4 sm:px-6 py-12 md:py-16"
      data-testid="thank-you-paid"
      dir="rtl"
    >
      {/* Crown — soft, sacred, NOT celebratory. A green check ring is
          appropriate for a confirmation moment without being festive,
          which would jar with "לעילוי נשמת" donations. */}
      <div className="text-center mb-6">
        <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7 text-emerald-500" strokeWidth={1.5} />
        </div>
      </div>

      {/* Emotional headline */}
      <h1
        className="font-heading text-3xl md:text-4xl text-center leading-tight mb-6"
        data-testid="thank-you-headline"
      >
        {headline.before}
        {headline.name && (
          <span className="text-gold"> {headline.name} </span>
        )}
        {headline.after}
      </h1>

      <div className="ornamental-divider mb-8">✦ ✦ ✦</div>

      {/* Verse — printed in verse-font with nikud, framed by soft gold
          rules above/below so it visually anchors the page. */}
      <div
        className="text-center mb-10 py-8 px-4 border-y border-gold/30"
        data-testid="thank-you-verse"
      >
        <p
          className="font-verse text-2xl md:text-3xl text-foreground leading-relaxed mb-3"
          lang="he"
        >
          {verse.he}
        </p>
        <p className="text-xs tracking-[0.18em] uppercase text-gold/80">
          {verse.ref}
        </p>
      </div>

      {/* Dedication certificate download — a single, restrained gold
          link below the verse. We use a plain <a> rather than a
          button because the destination IS a real file URL (better
          UX: works with browser "save as", "open in new tab", and
          screen-reader announcements). */}
      {certificateUrl && (
        <div className="text-center -mt-4 mb-10">
          <button
            type="button"
            onClick={() => setCertOpen(true)}
            className="inline-flex items-center gap-2 text-sm tracking-wide text-gold hover:text-gold/80 border-b border-gold/40 hover:border-gold pb-0.5 transition-colors"
            data-testid="thank-you-certificate-download"
          >
            <Download className="h-4 w-4" strokeWidth={1.5} />
            צפייה בתעודת ההקדשה (PDF)
          </button>
        </div>
      )}

      <CertificateViewer
        open={certOpen}
        url={certificateUrl}
        onClose={() => setCertOpen(false)}
        testid="thank-you-cert-viewer"
      />

      {/* Donation details — kept dense and quietly informative so it
          feels like an authentic receipt, not a marketing card. */}
      <div
        className="rounded-sm border border-border/60 bg-card/60 p-6 mb-8"
        data-testid="thank-you-details"
      >
        <h2 className="font-heading text-sm tracking-[0.25em] uppercase text-gold mb-4 text-center">
          פרטי התרומה
        </h2>
        <dl className="space-y-3 text-sm">
          <Row label="סכום">
            <span className="font-heading text-xl text-foreground">
              {CURRENCY_SYMBOL[donation.currency] || ""}
              {donation.amount}
              {donation.recurring && (
                <span className="text-xs text-muted-foreground ms-2">/ חודש</span>
              )}
            </span>
          </Row>
          {DEDICATION_LABEL[dedicationType] && dedicationText && (
            <Row label={DEDICATION_LABEL[dedicationType]}>{dedicationText}</Row>
          )}
          <Row label="תאריך עברי">{hebrewDate}</Row>
          <Row label="תאריך לועזי">{gregorianDate}</Row>
          {donation.receipt_number && (
            <Row label="מספר אסמכתא">
              <span className="font-mono text-xs">{donation.receipt_number}</span>
            </Row>
          )}
        </dl>
        {donation.donor_email && (
          <p
            className="text-xs text-muted-foreground text-center mt-5 pt-4 border-t border-border/40"
            data-testid="thank-you-email-confirm"
          >
            ✦ נשלחה קבלה ל-{donation.donor_email}
          </p>
        )}
      </div>

      {/* Action links — gentle, secondary tone. The primary value is
          the message above; these are just exit ramps. */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          asChild
          variant="outline"
          className="border-gold/40 hover:bg-gold/10 hover:border-gold"
          data-testid="thank-you-back-link"
        >
          <Link to="/beit-midrash">
            <BookOpen className="h-4 w-4 ms-2" strokeWidth={1.5} />
            חזרה לבית המדרש
          </Link>
        </Button>
        <Button
          onClick={onShare}
          variant="ghost"
          className="hover:bg-gold/10"
          data-testid="thank-you-share-btn"
        >
          <Share2 className="h-4 w-4 ms-2" strokeWidth={1.5} />
          שתף את האפליקציה
        </Button>
      </div>

      {/* Brand footer */}
      <div className="flex items-center justify-center gap-3 mt-12 opacity-70">
        <BrandMark size={32} className="rounded-md" />
        <span className="font-heading text-sm">תוריא · Torahya</span>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-2 last:border-0 last:pb-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground text-left font-heading">{children}</dd>
    </div>
  );
}

// ---------- Helpers ----------

/**
 * Build the headline as three pieces so we can give the dedication name
 * a soft gold accent in the middle. For dedications without a name we
 * fall back to the generic warm thanks.
 */
function buildHeadline(type, name) {
  if (type === "memory" && name) {
    return { before: "תרומתך לעילוי נשמת", name, after: "התקבלה" };
  }
  if (type === "honor" && name) {
    return { before: "תרומתך להצלחת", name, after: "התקבלה" };
  }
  if (type === "healing" && name) {
    return { before: "תרומתך לרפואת", name, after: "התקבלה" };
  }
  return { before: "תודה רבה על תרומתך", name: null, after: "" };
}

/**
 * Hebrew-calendar date formatting now lives in
 * `src/lib/hebrewGematria.js` — see `formatHebrewDate` import above.
 * Imported there to keep receipts, certificates, and this thank-you
 * page rendering dates in matching gematria letters
 * (e.g. `ד׳ בסיון תשפ״ו`), not Arabic numerals.
 */
